import React from "react";
import { Dialog } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

const isRenderableLogoSource = (value: string) =>
  /^(https?:\/\/|data:image\/|blob:)/i.test(value.trim());

export const getLogoInputValue = ({
  fileName,
  previewUrl,
}: {
  fileName: string;
  previewUrl: string;
}) => previewUrl || fileName;

export type DashboardLogoUploadFieldProps = {
  /** Field heading, e.g. "Member Logo" (the Members donor's hardcoded copy). */
  heading: React.ReactNode;
  id: string;
  fileName: string;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  onRemove?: () => void;
  previewAlt?: string;
  previewUrl?: string;
};

export const DashboardLogoUploadField = ({
  heading,
  id,
  fileName,
  onUpload,
  disabled,
  error,
  // Mock copy has no trailing period ("Ideally a square PNG or JPG image 5MB
  // in size"); the mock's double space after "square" was collapsed.
  helperText = "Ideally a square PNG or JPG image 5MB in size",
  onRemove,
  previewAlt = "Member logo preview",
  previewUrl,
}: DashboardLogoUploadFieldProps) => {
  const storedLogoSource = isRenderableLogoSource(fileName) ? fileName : "";
  const imageSource = previewUrl || storedLogoSource;
  // Deleting an uploaded logo must be confirmed first (MEM-CREATE-004); the
  // dialog mirrors the CMP destructive-confirm pattern (red circle icon,
  // DELETE/CANCEL) used by the Chamber Settings benefit delete modal.
  const [removeConfirmationOpen, setRemoveConfirmationOpen] =
    React.useState(false);

  const handleConfirmRemove = () => {
    setRemoveConfirmationOpen(false);
    onRemove?.();
  };

  return (
    <div className="mb-6">
      <p className="mb-1 text-sm font-bold text-black">{heading}</p>
      <p className="mb-3 text-xs text-black">{helperText}</p>
      <div className="rounded-md border border-dashed border-black bg-gray-50 p-4">
        {imageSource ? (
          <div className="flex min-h-32 flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex h-28 w-full items-center justify-center rounded-md border border-black bg-white p-2 sm:w-32">
              <img
                src={imageSource}
                alt={previewAlt}
                className="max-h-full max-w-full object-contain"
              />
            </div>
            {!disabled && (
              <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                <label
                  htmlFor={id}
                  className="inline-flex cursor-pointer items-center justify-center rounded-md border border-black bg-white px-4 py-2 text-sm font-bold text-black hover:bg-gray-100"
                >
                  Replace
                </label>
                {onRemove && (
                  <button
                    type="button"
                    onClick={() => setRemoveConfirmationOpen(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-red-600 bg-white px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50"
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <label
            htmlFor={id}
            className={`flex h-32 w-full flex-col items-center justify-center rounded-md text-black ${
              disabled
                ? "cursor-not-allowed opacity-60"
                : "cursor-pointer hover:bg-gray-100"
            }`}
          >
            <FileUploadOutlinedIcon />
            <span className="mt-2 text-sm font-semibold">
              Click Upload Logo
            </span>
          </label>
        )}
        <input
          id={id}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          disabled={disabled}
          onChange={onUpload}
        />
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <Dialog
        open={removeConfirmationOpen}
        onClose={() => setRemoveConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="relative rounded-xl bg-white px-10 py-12">
          <button
            type="button"
            onClick={() => setRemoveConfirmationOpen(false)}
            className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-2xl font-bold leading-none text-white transition hover:bg-red-600"
            aria-label="Close delete logo confirmation"
          >
            ×
          </button>
          <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-red-500 text-white">
            <DeleteOutlineIcon sx={{ fontSize: 58 }} />
          </div>
          <h2 className="mx-auto mt-10 max-w-lg text-center text-2xl font-bold leading-snug text-gray-950">
            Are you sure you want to delete this logo?
          </h2>
          <div className="mt-10 flex flex-col justify-center gap-5 sm:flex-row">
            <button
              type="button"
              onClick={handleConfirmRemove}
              className="inline-flex h-14 min-w-[200px] items-center justify-center gap-3 rounded-lg bg-red-500 px-6 text-base font-bold uppercase text-white shadow-md transition hover:bg-red-600"
            >
              <DeleteOutlineIcon fontSize="small" />
              DELETE
            </button>
            <button
              type="button"
              onClick={() => setRemoveConfirmationOpen(false)}
              className="inline-flex h-14 min-w-[200px] items-center justify-center rounded-lg bg-gray-950 px-6 text-base font-bold uppercase text-white shadow-md transition hover:bg-gray-800"
            >
              CANCEL
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
